import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Wordmark } from "@/components/wordmark";
import { redirectIfAuthenticated } from "@/lib/marketing-guards";



export const Route = createFileRoute("/login")({
  beforeLoad: redirectIfAuthenticated,
  head: () => ({
    meta: [
      { title: "Sign in, Super Affiliate OS" },
      {
        name: "description",
        content: "Sign in to your Super Affiliate OS account.",
      },
    ],
  }),
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate({ to: "/dashboard" });
  }, [loading, user, navigate]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Welcome back");
    navigate({ to: "/dashboard" });
  };

  return (
    <AuthShell title="Sign in to your account" subtitle="Welcome back to Super Affiliate OS">
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-foreground">
              Forgot?
            </Link>
          </div>
          <Input
            id="password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? "Signing in…" : "Sign in"}
        </Button>
        <p className="text-center text-sm text-muted-foreground">
          Don't have an account?{" "}
          <Link to="/signup" className="font-medium text-foreground hover:underline">
            Start your trial
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}

export function AuthShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-h-screen flex-col bg-background lg:flex-row">
      <div className="flex w-full flex-col lg:w-2/5">
        <header className="px-6 py-6 lg:px-10">
          <Link to="/" className="inline-flex items-center" aria-label="Home">
            <Wordmark />
          </Link>
        </header>
        <main className="flex flex-1 items-center justify-center px-6 py-10 lg:px-10">
          <div className="w-full max-w-sm">
            <div className="mb-8">
              <h1 className="text-2xl font-semibold tracking-tightest">{title}</h1>
              {subtitle && <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>}
            </div>
            {children}
          </div>
        </main>
      </div>
      <aside
        aria-hidden
        className="relative hidden overflow-hidden border-l border-border bg-card/30 lg:flex lg:w-3/5"
      >
        <div className="marketing-mesh marketing-mesh-anim absolute -inset-32 opacity-50" />
        <div className="relative z-10 m-auto max-w-md px-10">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">
            From a $1M+ super affiliate
          </div>
          <p className="mt-4 text-2xl font-medium leading-[1.4] tracking-tight text-foreground/90">
            "I accepted whatever the dashboards told me. For ten years. Looking back, the
            unreported losses add up to something north of seven figures."
          </p>
          <div className="mt-6 text-sm text-muted-foreground">
           , Why Super Affiliate OS exists.
          </div>
        </div>
      </aside>
    </div>
  );
}
